import React, {useEffect, useMemo, useRef, useState} from 'react';
import {classes, style, vars} from './doppe-layout8-profile-view-template.st.css';
import {BymoTemplateMetadataInferBymoPageProps, useBymoPageContextProps} from '../../../doppe-sdk';
import {ImageViewPositionVertical, useElementClientAreaBinding} from '@wix/devzai-utils-react';
import {arrayFirst, arrayIsNotNullableOrEmpty, stringIsNotNullableOrWhiteSpace} from '@wix/devzai-utils-common';
import {FlexLayout} from '@wix/devzai-common-client';
import {
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisible,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../../client-server-common/types/doppe-hideable-value';
import {DoppeHideableValueRenderer} from '../../doppe-hideable-value-renderer/doppe-hideable-value-renderer';
import {
    DoppePageProfileNameView,
    DoppePageProfileNameViewVerifiedCheckAppearance
} from '../../doppe-page-profile-name-view/doppe-page-profile-name-view';
import {bymoPageSocialLinkIsHidden, DoppeProfileNameAutoSource} from '../../../client-server-common';
import {
    BymoTemplateSpeedDialTemplateMetadata
} from '../../bymo-templates/bymo-template-speed-dial/bymo-template-speed-dial.metadata';
import {DoppeLayoutRoot} from '../dopp-layouts-components/doppe-layout-root/doppe-layout-root';
import {DoppLayoutContent} from '../dopp-layouts-components/dopp-layout-content/dopp-layout-content';
import {DoppLayoutBackground} from '../dopp-layouts-components/dopp-layout-background/dopp-layout-background';
import {
    DoppLayoutSeparator,
    DoppLayoutSeparatorSkin
} from '../dopp-layouts-components/dopp-layout-separator/dopp-layout-separator';
import {
    DoppLayoutBackgroundMedia
} from '../dopp-layouts-components/dopp-layout-background-media/dopp-layout-background-media';
import {DoppePageProfilePicture} from '../../doppe-page-profile-picture/doppe-page-profile-picture';
import {doppeViewerShouldRemoveHoppBanner} from '../../doppe-viewer-utils/doppe-viewer-utils';
import {DoppeBackgroundView} from '../../doppe-background-view/doppe-background-view';
import {DoppeLayoutPageContent} from '../dopp-layouts-components/doppe-layout-page-content/doppe-layout-page-content';
import {DoppeLayoutBioText} from '../dopp-layouts-components/doppe-layout-bio-text/doppe-layout-bio-text';
import {DoppeLayoutSocialLinks} from '../dopp-layouts-components/doppe-layout-social-links/doppe-layout-social-links';

export const DoppeLayout8ProfileViewTemplate = React.memo(function DoppeLayout8ProfileViewTemplate(props: BymoTemplateMetadataInferBymoPageProps<typeof BymoTemplateSpeedDialTemplateMetadata>) {
    const {
        profilePictureLinkInBio,
        bioText,
        profileName,
        bioTextMaxLines,
        socialLinks,
        heroPhotos,
    } = props;


    const contextProps = useBymoPageContextProps();

    const bioTextVisibleValue = doppeHideableValueGetVisibleValue(bioText, '');

    const visibleSocialLinks = socialLinks.filter(socialLink => !bymoPageSocialLinkIsHidden(socialLink));

    const showProfileSeparator =
        stringIsNotNullableOrWhiteSpace(bioTextVisibleValue) || arrayIsNotNullableOrEmpty(visibleSocialLinks) || doppeHideableValueIsVisibleAndNotEqualValue(profileName, '') || doppeHideableValueIsVisible(profilePictureLinkInBio)

    const hasProfileImage = useMemo(() => {
        return doppeHideableValueIsVisible(profilePictureLinkInBio);
    }, [profilePictureLinkInBio]);

    const removeHoppBanner = doppeViewerShouldRemoveHoppBanner(props, contextProps);

    const [blurValue, setBlurValue] = useState(0);

    const contentRef = useRef<HTMLDivElement>(null);
    const clientArea = useElementClientAreaBinding(contentRef) ?? 0;

    useEffect(() => {
        if (clientArea === 0) {
            setBlurValue(0)
        }
        else {
            setBlurValue(Math.min(-clientArea.top / clientArea.height, 1) * 6);
        }
    }, [clientArea]);

    const ContentComponent = (
        <DoppLayoutContent
            className={style(classes.firstContent, FlexLayout.column.default)}
        >
            <div
                className={style(classes.profileView, FlexLayout.column.default)}
            >
                <div
                    className={style(classes.profileViewContent, {}, FlexLayout.column.alignToStart)}
                >
                    {
                        hasProfileImage ?
                            <DoppeHideableValueRenderer
                                hideableValue={profilePictureLinkInBio}
                                renderFunction={profilePicture => (
                                    <DoppePageProfilePicture
                                        className={style(classes.profileImage, {})}
                                        profilePicture={profilePicture}
                                        size={74}
                                    />
                                )}
                            />
                            :
                            null
                    }
                    <DoppePageProfileNameView
                        className={style(classes.profileName, {}, FlexLayout.verticallyJustifyToEnd)}
                        defaultProfileNameAutoSource={DoppeProfileNameAutoSource.AccountName}
                        verifiedCheckAppearance={DoppePageProfileNameViewVerifiedCheckAppearance.Superscript}
                    />
                    <DoppeLayoutBioText
                        className={style(classes.bioText, {})}
                        bioText={bioText}
                        bioTextMaxLines={bioTextMaxLines}
                        lineHeight={'18px'}
                    />
                    <DoppeLayoutSocialLinks
                        className={style(classes.socialLinks, {}) }
                        socialLinks={socialLinks}
                    />
                </div>

            </div>
        </DoppLayoutContent>
    )

    return (
        <DoppeLayoutRoot
            className={style(classes.root, {
                hideFooterBanner: removeHoppBanner,
            })}
            style={{
                [vars.blur]: `${blurValue}px`
            }}
            hideHoppBanner={props.removeHoppBanner}
        >
            <DoppLayoutBackground
                className={style(classes.coverBackground)}
            >
                <div
                    className={style(classes.coverImageContainer, {})}
                >
                    <DoppeBackgroundView
                        className={style(classes.heroPhoto)}
                        verticalPosition={ImageViewPositionVertical.Top}
                        heroPhoto={arrayFirst(heroPhotos ?? [])}
                    />
                </div>
            </DoppLayoutBackground>
            <DoppLayoutBackground
                className={style(classes.coverBackground)}
            >
                <div className={style(classes.fullScreenHeight, {})}/>
                <div
                    className={style(classes.firstContentWrapperNew, {}, FlexLayout.column.default)}
                >
                    <div
                        className={style(classes.hiddenContent, {}, FlexLayout.noShrink)}
                    >
                        {ContentComponent}
                    </div>
                    <DoppLayoutBackgroundMedia
                        className={style(classes.pageWithCoverBackgroundView, {})}
                    />

                </div>
            </DoppLayoutBackground>
            <div
                className={style(classes.firstContentWrapper, {}, FlexLayout.column.default)}
            >
                <div
                    ref={contentRef}
                    className={style(classes.spacer, {}, FlexLayout.fillRemainingVerticalSpace)}/>
                {ContentComponent}
            </div>
            <DoppLayoutContent
                className={style(classes.content)}
            >
                {showProfileSeparator ? (
                    <DoppLayoutSeparator
                        className={style(classes.separator, {})}
                        skin={DoppLayoutSeparatorSkin.ProfileNoHorizontalMargin}
                    />
                ) : null}
                <DoppeLayoutPageContent
                    className={style(classes.pageContent, {})}
                    pageProps={props}
                />
            </DoppLayoutContent>
        </DoppeLayoutRoot>
    )
});

